import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    Camera,
    CameraOff,
    Check,
    Download,
    EyeOff,
    LayoutGrid,
    List,
    Users as UsersIcon,
} from "lucide-react";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import Icon from "@/components/ui/Icon.jsx";
import { SearchInput, Select, Switch } from "@/components/ui/Form.jsx";
import { Callout, EmptyState } from "@/components/ui/Display.jsx";
import Pagination, { PaginationInfo } from "@/components/ui/Pagination.jsx";
import Tag from "@/components/ui/Tag.jsx";
import DateRangePicker from "@/components/ui/DateRangePicker.jsx";
import AutoRefresh from "@/components/dashboard/AutoRefresh.jsx";
import ShotCard from "@/components/screenshots/ShotCard.jsx";
import ShotStat from "@/components/screenshots/ShotStat.jsx";
import ShotRow from "@/components/screenshots/ShotRow.jsx";
import ScreenshotViewer from "@/components/screenshots/ScreenshotViewer.jsx";

import { screenshotService, userService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { organizationQuery } from "@/lib/queries.js";
import { useUrlRefresh } from "@/hooks/useUrlRefresh.js";
import { useAuth } from "@/context/auth.js";
import { dayjs, formatDate } from "@/lib/format.js";
import { downloadCsv } from "@/lib/csv.js";
import { featureGates } from "@/lib/userDetail.js";
import { activeOnly } from "@/lib/users.js";
import {
    PRODUCTIVITY_OPTIONS,
    SHOT_CSV_HEADERS,
    flattenShots,
    groupShots,
    shotsToRows,
} from "@/lib/screenshots.js";

/*
 * Replaces frontend/app/(dashboard)/screenshots/page.js.
 *
 * ONE DAY AT A TIME, AND THE WHOLE OF IT.
 *
 * The API paginates server-side but cannot filter by user, app, productivity or
 * blur, and cannot search — so those filters have to run in the browser. Mixing
 * the two is what makes a screenshot page lie: filter a 24-item server page and
 * it reports "3 screenshots" while a hundred more matches sit on page 2, and
 * every stat card above is computed from that same partial page.
 *
 * So the day is fetched in one request and everything — filtering, the stats,
 * grouping, paging — happens over the full day, exactly as the markup does. The
 * day is a natural bound: one organization captures a few hundred records in a
 * working day. If a day ever exceeds the cap, the page says so out loud rather
 * than quietly showing a slice.
 *
 * The one filter that IS pushed to the server is the user, because
 * /screenshots/user/:id exists and filtering there means the cap applies to
 * that person's day instead of everyone's.
 */
const REFRESH_MS = 60_000;
// Round numbers, and a 500 for the days someone wants the whole thing on one
// page. The cap above is 1,000 records, so 500 is the largest size that can
// still be a page rather than the entire load.
const PAGE_SIZES = [25, 50, 100, 500];

/*
 * 100 by default — not the first option. A screenshot grid is scanned, not
 * read: you scroll looking for the one that answers your question, and 25 puts
 * a pagination click between you and it four times more often. The options stay
 * in ascending order because that is how a size list reads.
 */
const DEFAULT_PAGE_SIZE = 100;

const GROUPS = [
    { value: "hour", label: "Hour" },
    { value: "day", label: "Day" },
    { value: "user", label: "User" },
    { value: "none", label: "Nothing" },
];

// Form field → query-string key. The names differ because a URL reads better
// as `?q=github&blurred=1` than as `?search=github&blurredOnly=true`.
const PARAM_OF = {
    search: "q",
    user: "user",
    app: "app",
    productivity: "productivity",
    blurredOnly: "blurred",
};

const NONE = [];

/*
 * THE URL IS THE STATE.
 *
 * Every filter, the date, the view, the grouping and the page live in the query
 * string rather than in component state, so what you are looking at can be
 * reloaded, bookmarked and pasted to a colleague. It also makes the link the
 * user page already had — "Open full screenshot browser" → /screenshots?user=…
 * — actually do something; it was being ignored.
 *
 * Defaults are NOT written. A URL that says everything is at its default is
 * noise, and `?page=1&size=25&view=grid&group=hour` is harder to read than
 * `/screenshots`.
 */
const DEFAULTS = {
    q: "",
    from: "",
    to: "",
    user: "",
    app: "",
    productivity: "",
    blurred: "",
    view: "grid",
    group: "hour",
    page: "1",
    size: String(DEFAULT_PAGE_SIZE),
};

export default function Screenshots() {
    const queryClient = useQueryClient();
    const { user: me } = useAuth();
    const [params, setParams] = useSearchParams();

    const today = useMemo(() => dayjs().format("YYYY-MM-DD"), []);
    const read = (key) => params.get(key) ?? DEFAULTS[key];

    /*
     * A RANGE, not a day.
     *
     * `?date=` is still honoured as a one-day range: the user page has always
     * linked here with it, and so have bookmarks. It simply becomes from = to.
     */
    const legacyDate = params.get("date");
    const from = params.get("from") || legacyDate || today;
    const to = params.get("to") || legacyDate || today;

    // A range that ends before it starts returns nothing and looks broken, so
    // the later of the two wins for both ends.
    const startDate = from <= to ? from : to;
    const endDate = from <= to ? to : from;
    const multiDay = startDate !== endDate;

    const view = read("view") === "list" ? "list" : "grid";
    const groupBy = ["hour", "day", "user", "none"].includes(read("group"))
        ? read("group")
        : "hour";
    const page = Math.max(1, Number(read("page")) || 1);
    const pageSize = PAGE_SIZES.includes(Number(read("size")))
        ? Number(read("size"))
        : DEFAULT_PAGE_SIZE;

    const filters = useMemo(
        () => ({
            search: params.get("q") ?? "",
            user: params.get("user") ?? "",
            app: params.get("app") ?? "",
            productivity: params.get("productivity") ?? "",
            blurredOnly: params.get("blurred") === "1",
        }),
        [params],
    );

    /**
     * Write a patch into the query string, dropping anything that is at its
     * default so the URL stays readable.
     *
     * `replace` for the search box: pushing a history entry per keystroke would
     * make the Back button walk backwards through a word one letter at a time.
     * Everything else pushes, so Back undoes the filter you just applied.
     */
    const setParam = (patch, { replace = false } = {}) => {
        const next = new URLSearchParams(params);

        for (const [key, value] of Object.entries(patch)) {
            const asString = value == null ? "" : String(value);
            const isDefault =
                key === "from" || key === "to"
                    ? asString === today
                    : asString === (DEFAULTS[key] ?? "");
            if (!asString || isDefault) next.delete(key);
            else next.set(key, asString);
        }

        // Writing a range supersedes the legacy single-day parameter; leaving
        // both would make the URL contradict itself.
        if ("from" in patch || "to" in patch) next.delete("date");

        setParams(next, { replace });
    };

    /*
     * The search box types locally and reaches the URL 400ms later.
     *
     * It used to write on every keystroke, which was free while filtering
     * happened in the browser and is a request per character now that it does
     * not — "github.com" would be ten round trips, each one re-signing a page
     * of S3 URLs.
     *
     * The URL stays the source of truth: when it changes from outside — Back,
     * Reset, a pasted link — the box follows it. That comparison happens during
     * render rather than in an effect, which is React's documented way to react
     * to a changed input and avoids rendering the stale value first.
     */
    const [searchInput, setSearchInput] = useState(filters.search);
    const [urlSearch, setUrlSearch] = useState(filters.search);

    if (urlSearch !== filters.search) {
        setUrlSearch(filters.search);
        setSearchInput(filters.search);
    }

    // The OPEN SHOT, not its position. An index would silently point at a
    // different screenshot the moment a filter, a page change or a regrouping
    // reordered the list underneath the modal.
    const [viewerKey, setViewerKey] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(false);

    const usersQuery = useQuery({
        queryKey: ["users"],
        queryFn: () => userService.getAllUsers().then((r) => r.data?.data?.users ?? []),
    });
    const users = usersQuery.data ?? NONE;

    /*
     * The organization, for the Key Log and Clipboard gates in the viewer.
     *
     * This list mixes people, and both features resolve per USER (override
     * first, organization second, entitlement over the top for clipboard) — so
     * an empty panel can only name the switch that is actually closed if both
     * halves are known. Without this the message blamed the organization even
     * when the organization had the feature ON and one person's override was
     * what held it shut.
     */
    const organizationId =
        typeof me?.organization === "object" ? me?.organization?._id : me?.organization;

    const orgQuery = useQuery(organizationQuery(organizationId));

    const settingsByUser = useMemo(
        () => new Map(users.map((u) => [String(u._id ?? u.id), u.settings ?? {}])),
        [users],
    );

    const gatesFor = (shot) => {
        const org = orgQuery.data?.organization;
        if (!org) return null;
        return featureGates(org.settings, settingsByUser.get(String(shot.user?.id)), org);
    };

    /*
     * Two endpoints behind one query key. The per-user one omits the `user`
     * object from every record (it was filtered by id, so the server saw no
     * reason to repeat it) — hence the selected user is handed to flattenShots
     * as the fallback, or the whole page would render "Unknown".
     */
    const selectedUser = users.find((u) => (u._id ?? u.id) === filters.user);

    /*
     * EVERY FILTER GOES TO THE SERVER, and so does the paging.
     *
     * This page used to fetch the whole day — a thousand records, capped — and
     * filter in the browser, because the API could not. That cost a megabyte
     * and a thousand freshly signed S3 URLs to show twenty-five pictures, and
     * it forced the page to admit "only the most recent 1000 were loaded".
     *
     * The endpoints take the filters now, so a page is a page of MATCHES. The
     * stat cards stay honest because the response carries a `summary`
     * aggregated over the whole filtered range, not over these rows.
     */
    const shotsQuery = useQuery({
        queryKey: ["screenshots", startDate, endDate, filters, page, pageSize],
        queryFn: async () => {
            const params = {
                startDate,
                endDate,
                limit: pageSize,
                page,
                ...(filters.app ? { application: filters.app } : {}),
                ...(filters.productivity ? { productivityStatus: filters.productivity } : {}),
                ...(filters.blurredOnly ? { blurred: "true" } : {}),
                ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
            };

            // The per-user endpoint scopes by path; the organization one takes
            // the id as a filter. Same filters either way.
            const response = filters.user
                ? await screenshotService.getUserScreenshots(filters.user, params)
                : await screenshotService.getOrganizationScreenshots(params);

            return {
                records: response.data?.data?.screenshots ?? [],
                total: response.data?.total ?? 0,
                summary: response.data?.summary ?? null,
            };
        },
        // Keep the previous page on screen while the next one loads, rather
        // than blanking the grid on every page click.
        placeholderData: (previous) => previous,
        refetchInterval: autoRefresh ? REFRESH_MS : false,
    });

    const records = shotsQuery.data?.records ?? NONE;
    const serverTotal = shotsQuery.data?.total ?? 0;
    const summary = shotsQuery.data?.summary ?? null;

    const shots = useMemo(
        () =>
            flattenShots(
                records,
                selectedUser
                    ? {
                          id: selectedUser._id ?? selectedUser.id,
                          name: selectedUser.name,
                          email: selectedUser.email,
                      }
                    : null,
            ),
        [records, selectedUser],
    );

    /*
     * The rows ARE the page — the server already applied every filter, so there
     * is nothing left to narrow here. The old client-side pass has gone with
     * the whole-day fetch that made it necessary.
     *
     * The figures come from the response's summary, which the server aggregates
     * over the entire filtered range. Counting these twenty-five rows instead
     * would turn "3 users captured" into a statement about one page.
     */
    const stats = useMemo(
        () => ({
            total: summary?.images ?? shots.length,
            users: summary?.users ?? 0,
            productivePercent: summary?.records
                ? Math.round((summary.productive / summary.records) * 100)
                : 0,
            blurred: summary?.blurred ?? 0,
        }),
        [summary, shots.length],
    );

    /*
     * The application list comes from the summary too. Built from the loaded
     * rows it would only offer the apps on THIS page, so filtering to anything
     * else would have meant paging around until it appeared in the dropdown.
     */
    const apps = useMemo(() => [...(summary?.applications ?? [])].sort(), [summary]);

    const totalPages = Math.max(1, Math.ceil(serverTotal / pageSize));
    const current = Math.min(page, totalPages);

    /*
     * Grouping REORDERS: "by user" pulls a person's captures together, so the
     * flat page order and the rendered order stop matching. The viewer's ←/→
     * walk this flattened array — the one actually on screen — so pressing → is
     * always the card to the right, whatever the grouping.
     */
    const groups = useMemo(
        () => groupShots(shots, groupBy, { multiDay }),
        [shots, groupBy, multiDay],
    );

    const visible = useMemo(() => groups.flatMap((g) => g.items), [groups]);

    // Derived, so a shot that filtering or paging removed simply closes the
    // viewer — no effect needed to chase the change.
    const viewerIndex = viewerKey ? visible.findIndex((s) => s.key === viewerKey) : -1;

    // Any filter change returns to page 1: staying on page 7 of a list that now
    // has two pages shows an empty grid and looks like "no results".
    const setFilter = (key, value) =>
        setParam({ [PARAM_OF[key]]: value, page: 1 }, { replace: key === "search" });

    useEffect(() => {
        if (searchInput === filters.search) return;
        const id = window.setTimeout(() => setFilter("search", searchInput), 400);
        return () => window.clearTimeout(id);
        // setFilter closes over `params`; re-running on every change of it would
        // restart the timer mid-word.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    const setPage = (next) => setParam({ page: next });

    const resetFilters = () =>
        setParam({ q: "", user: "", app: "", productivity: "", blurred: "", page: 1 });

    const filtersActive =
        filters.search !== "" ||
        filters.user !== "" ||
        filters.app !== "" ||
        filters.productivity !== "" ||
        filters.blurredOnly;

    /*
     * A signed URL that has expired takes the whole page's URLs with it — they
     * were all signed in the same response, within a second of each other. So
     * one image failing refetches the query rather than that image, and every
     * thumbnail gets a fresh link.
     *
     * BOUNDED, because the refetch re-signs every URL and so clears
     * ProxyImage's per-URL retry guard — one dead object could otherwise
     * refetch forever. See hooks/useUrlRefresh.js.
     */
    const refreshUrls = useUrlRefresh(
        useCallback(
            () =>
                queryClient.invalidateQueries({
                    queryKey: ["screenshots", startDate, endDate],
                }),
            [queryClient, startDate, endDate],
        ),
        `${startDate}|${endDate}|${filters.user}`,
    );

    /*
     * The page's rows, not the whole range — the rest are not in the browser
     * any more. The filename says which page, so two exports of a filtered day
     * do not overwrite each other and neither claims to be the lot.
     */
    const exportCsv = () =>
        downloadCsv(
            `screenshots-${startDate}${multiDay ? `_to_${endDate}` : ""}-page-${current}`,
            SHOT_CSV_HEADERS,
            shotsToRows(shots),
        );

    return (
        <>
            <PageChrome breadcrumb={["Screenshots"]} title="Screenshots">
                <>
                    <AutoRefresh
                        enabled={autoRefresh}
                        onToggle={setAutoRefresh}
                        intervalMs={REFRESH_MS}
                        lastUpdated={shotsQuery.dataUpdatedAt}
                    />

                    <Button
                        variant="primary"
                        onClick={() => shotsQuery.refetch()}
                        loading={shotsQuery.isFetching}
                        data-refresh
                    >
                        <Icon name="clock" className="size-4" />
                        Refresh
                    </Button>
                </>
            </PageChrome>

            <main className="mx-auto flex w-full max-w-[1680px] flex-col gap-5 p-4 lg:p-5">
                {/* ---- Page head ---- */}
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h1 className="flex flex-wrap items-center gap-2.5 text-[22px] font-semibold tracking-tight">
                            Screenshots
                            {selectedUser ? (
                                <Tag tone="emerald" data-scope>
                                    {selectedUser.name}
                                </Tag>
                            ) : (
                                <Tag tone="brand" data-scope>
                                    All Organization
                                </Tag>
                            )}
                        </h1>
                        <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                            Captured while tracking is active ·{" "}
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                                {multiDay
                                    ? `${formatDate(startDate)} — ${formatDate(endDate)}`
                                    : formatDate(startDate)}
                            </span>
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2.5">
                        <div
                            className="inline-flex gap-0.5 rounded-md bg-slate-200/70 p-0.5 dark:bg-slate-800"
                            role="group"
                            aria-label="View"
                        >
                            <ViewButton
                                active={view === "grid"}
                                onClick={() => setParam({ view: "grid" })}
                                label="Grid"
                                icon={<LayoutGrid className="size-3.5" strokeWidth={2} />}
                            />
                            <ViewButton
                                active={view === "list"}
                                onClick={() => setParam({ view: "list" })}
                                label="List"
                                icon={<List className="size-3.5" strokeWidth={2} />}
                            />
                        </div>

                        <Button onClick={exportCsv} disabled={!shots.length} data-export>
                            <Download className="size-4" strokeWidth={2} />
                            Export
                        </Button>
                    </div>
                </div>

                {/* ---- Stats. Computed over the FILTERED day, so they always
                       describe what is on screen. ---- */}
                <section className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
                    <ShotStat
                        label="Screenshots"
                        value={stats.total}
                        tone="brand"
                        icon={<Camera className="size-4.5" strokeWidth={2} />}
                    />
                    <ShotStat
                        label="Users captured"
                        value={stats.users}
                        tone="slate"
                        icon={<UsersIcon className="size-4.5" strokeWidth={2} />}
                    />
                    <ShotStat
                        label="Productive shots"
                        value={`${stats.productivePercent}%`}
                        tone="emerald"
                        tintValue
                        icon={<Check className="size-4.5" strokeWidth={2.2} />}
                    />
                    <ShotStat
                        label="Blurred (privacy)"
                        value={stats.blurred}
                        tone="amber"
                        tintValue
                        icon={<EyeOff className="size-4.5" strokeWidth={2} />}
                    />
                </section>

                {/* ---- Filters ---- */}
                <Card>
                    <div className="flex flex-wrap items-center gap-2.5 p-3.5">
                        <SearchInput
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search window title, app or URL…"
                            aria-label="Search screenshots"
                            className="w-72"
                            data-filter="search"
                        />

                        <Select
                            value={filters.user}
                            onChange={(e) => setFilter("user", e.target.value)}
                            aria-label="Filter by user"
                            className="w-52"
                            data-filter="user"
                        >
                            <option value="">All Organization</option>
                            {/*
                             * Active only, as the page this replaces did. No
                             * deactivated account in the reference database
                             * holds a single capture, so nothing becomes
                             * unreachable — but if that ever changes, this is
                             * the line that hides it.
                             */}
                            {activeOnly(users).map((u) => (
                                <option key={u._id ?? u.id} value={u._id ?? u.id}>
                                    {u.name}
                                </option>
                            ))}
                        </Select>

                        <Select
                            value={filters.app}
                            onChange={(e) => setFilter("app", e.target.value)}
                            aria-label="Filter by application"
                            className="w-44"
                            data-filter="app"
                        >
                            <option value="">All applications</option>
                            {apps.map((a) => (
                                <option key={a} value={a}>
                                    {a}
                                </option>
                            ))}
                        </Select>

                        <Select
                            value={filters.productivity}
                            onChange={(e) => setFilter("productivity", e.target.value)}
                            aria-label="Filter by productivity"
                            className="w-40"
                            data-filter="productivity"
                        >
                            {PRODUCTIVITY_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>

                        {/* One field, both ends, and the shortcuts inside it —
                            see components/ui/DateRangePicker. */}
                        <DateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            max={today}
                            onChange={(next) =>
                                setParam({ from: next.startDate, to: next.endDate, page: 1 })
                            }
                            className="w-64"
                        />

                        <Switch
                            label="Blurred only"
                            tone="amber"
                            checked={filters.blurredOnly}
                            // "1" or absent, so the URL reads ?blurred=1
                            // rather than ?blurredOnly=false on every page.
                            onChange={(e) => setFilter("blurredOnly", e.target.checked ? "1" : "")}
                            data-filter="blurred"
                        />

                        <Button
                            variant="link"
                            className="ml-auto"
                            onClick={resetFilters}
                            disabled={!filtersActive}
                            data-reset
                        >
                            Reset filters
                        </Button>
                    </div>
                </Card>

                {shotsQuery.isError && (
                    <Callout tone="danger" data-error>
                        {apiErrorMessage(shotsQuery.error, "Failed to load screenshots.")}{" "}
                        <button
                            type="button"
                            onClick={() => shotsQuery.refetch()}
                            className="font-semibold underline"
                        >
                            Try again
                        </button>
                    </Callout>
                )}

                {/* ---- Results ---- */}
                <Card>
                    <CardHead className="flex-wrap">
                        {/*
                         * The MATCH count, from the server. `records` are
                         * capture moments and `images` are pictures — a
                         * two-monitor capture is one of the first and two of
                         * the second — so both are named rather than leaving
                         * the reader to wonder why the cards outnumber the
                         * total.
                         */}
                        <CardTitle
                            hint={
                                summary && summary.images !== summary.records
                                    ? `${summary.images} images across ${summary.records} captures`
                                    : undefined
                            }
                        >
                            <span data-result-count>
                                {serverTotal} capture{serverTotal === 1 ? "" : "s"}
                            </span>
                        </CardTitle>
                        <CardExtra>
                            <label className="flex items-center gap-2 text-[12.5px] text-slate-500 dark:text-slate-400">
                                Group by
                                <Select
                                    size="sm"
                                    value={groupBy}
                                    onChange={(e) => setParam({ group: e.target.value, page: 1 })}
                                    aria-label="Group by"
                                    className="w-auto"
                                    data-groupby
                                >
                                    {GROUPS.map((g) => (
                                        <option key={g.value} value={g.value}>
                                            {g.label}
                                        </option>
                                    ))}
                                </Select>
                            </label>
                        </CardExtra>
                    </CardHead>

                    {visible.length === 0 ? (
                        <div className="p-4.5">
                            <EmptyState
                                icon={
                                    <CameraOff className="size-10 opacity-50" strokeWidth={1.5} />
                                }
                                title={
                                    shotsQuery.isLoading
                                        ? "Loading screenshots…"
                                        : filtersActive
                                          ? "No screenshots match these filters"
                                          : "No screenshots captured on this day"
                                }
                                hint={
                                    !shotsQuery.isLoading && !filtersActive
                                        ? "Screenshots appear here once someone tracks time with capture enabled."
                                        : undefined
                                }
                                action={
                                    filtersActive ? (
                                        <Button onClick={resetFilters}>Clear filters</Button>
                                    ) : undefined
                                }
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-6 p-4.5">
                            {groups.map((group) => (
                                <section key={group.key || "all"} data-group={group.key}>
                                    {group.label && (
                                        <div className="mb-3 flex items-center gap-3">
                                            <h3 className="text-[13px] font-semibold text-slate-700 dark:text-slate-300">
                                                {group.label}
                                            </h3>
                                            <Tag tone="slate">{group.items.length}</Tag>
                                            <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
                                        </div>
                                    )}

                                    {view === "grid" ? (
                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                                            {group.items.map((shot) => (
                                                <ShotCard
                                                    key={shot.key}
                                                    shot={shot}
                                                    onOpen={() => setViewerKey(shot.key)}
                                                    onExpired={refreshUrls}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
                                            {group.items.map((shot) => (
                                                <ShotRow
                                                    key={shot.key}
                                                    shot={shot}
                                                    onOpen={() => setViewerKey(shot.key)}
                                                    onExpired={refreshUrls}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </section>
                            ))}
                        </div>
                    )}

                    <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 px-4.5 py-3 dark:border-slate-800">
                        <PaginationInfo
                            page={current}
                            pageSize={pageSize}
                            total={serverTotal}
                            sizes={PAGE_SIZES}
                            noun="screenshots"
                            onPageSizeChange={(size) => setParam({ size, page: 1 })}
                        />
                        <div className="ml-auto">
                            <Pagination page={current} totalPages={totalPages} onChange={setPage} />
                        </div>
                    </div>
                </Card>
            </main>

            {viewerIndex >= 0 && (
                <ScreenshotViewer
                    shots={visible}
                    index={viewerIndex}
                    gatesFor={gatesFor}
                    onIndexChange={(next) => {
                        const shot = visible[next];
                        if (shot) setViewerKey(shot.key);
                    }}
                    onClose={() => setViewerKey(null)}
                    onExpired={refreshUrls}
                />
            )}
        </>
    );
}

function ViewButton({ active, onClick, label, icon }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            data-view={label.toLowerCase()}
            className={
                active
                    ? "flex items-center gap-1.5 rounded bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-900 shadow-xs dark:bg-slate-700 dark:text-white"
                    : "flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
            }
        >
            {icon}
            {label}
        </button>
    );
}
